/* import __COLOCATED_TEMPLATE__ from './sunshine-api.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type IntlService from 'ember-intl/services/intl';
import type SunshineConfiguration from 'embercom/models/standalone/sunshine-configuration';
import type ZendeskConfiguration from 'embercom/models/standalone/zendesk-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import safeWindowOpen from 'embercom/lib/safe-window-open';

export interface Args {
  stackVertically?: boolean;
  hideBorder?: boolean;
  hideTitle?: boolean;
  closeOnSave?: () => void;
}

export default class SunshineApi extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare notificationsService: {
    notifyConfirmation: (message: string) => void;
    notifyError: (message: string) => void;
    notifyResponseError: (
      error: Error,
      messages: { default: string },
      options: { responseProvidedErrors: number[] },
    ) => void;
  };

  @tracked isRevokingAccess = false;
  @tracked isSaving = false;
  @tracked isConnecting = false;
  private oauthWindow: Window | null | undefined = null;
  private oauthMessageHandler?: (
    event: MessageEvent<{ type: 'OAUTH_SUCCESS' | 'OAUTH_ERROR'; error?: string }>,
  ) => void;

  get isDisconnected() {
    return !this.sunshineConfig || this.sunshineConfig.isSunshineDisconnected;
  }

  get zendeskPlatform() {
    return this.finStandaloneService.zendeskPlatform;
  }

  get shouldShowConnectButton() {
    return this.isDisconnected || this.isSaving || this.isConnecting;
  }

  get shouldShowRevokeButton() {
    return !this.isSaving && !this.isConnecting;
  }

  get shouldShowOAuthUI() {
    return this.zendeskConfig?.shouldShowSunshineOAuthUI;
  }

  willDestroy() {
    super.willDestroy();
    this.cleanupOAuthState();
  }

  @action async save() {
    this.isSaving = true;
    try {
      await this.zendeskPlatform.saveZendeskConfig();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.zendesk.settings.sunshine-api.save-success'),
      );
      this.args.closeOnSave?.();
    } finally {
      await this.finStandaloneService.zendeskConfig!.reload();
      this.isSaving = false;
    }
  }

  @action async connectWithOAuth() {
    this.isConnecting = true;

    try {
      // Set up message handler for OAuth callback
      this.setupOAuthMessageHandler();

      // Open OAuth flow in popup
      let uri = '/standalone/zendesk/sunshine_connect';
      let params = `app_id_code=${this.finStandaloneService.app.id}`;
      let oauthUrl = `${uri}?${params}`;

      // Open popup window for OAuth
      this.oauthWindow = safeWindowOpen(
        oauthUrl,
        'zendesk-sunshine-oauth',
        'width=600,height=700,scrollbars=yes,resizable=yes',
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('standalone.zendesk.settings.connect.oauth-error'),
      );
      this.isConnecting = false;
    }
  }

  @action async revokeAccess() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t('standalone.zendesk.settings.sunshine-api.confirm-body'),
      confirmButtonText: this.intl.t('standalone.zendesk.settings.sunshine-api.confirm-button'),
    });

    try {
      if (isConfirmed) {
        this.isRevokingAccess = true;
        await this.zendeskConfig.revokeSunshine();
        this.notificationsService.notifyConfirmation(
          this.intl.t('standalone.zendesk.settings.sunshine-api.revoke-success-notification'),
        );
      }
    } catch (e) {
      console.error(e.message || e.jqXHR);
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t(
            'standalone.zendesk.settings.sunshine-api.revoke-failure-notification',
          ),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
    } finally {
      this.isRevokingAccess = false;
    }
  }

  get sunshineConfig(): SunshineConfiguration | undefined {
    return this.zendeskPlatform.zendeskConfig!.sunshineIntegration;
  }

  get zendeskConfig(): ZendeskConfiguration {
    return this.zendeskPlatform.zendeskConfig!;
  }

  get removeButtonLabel() {
    return this.isRevokingAccess
      ? this.intl.t('standalone.zendesk.settings.sunshine-api.revoking')
      : this.intl.t('standalone.zendesk.settings.sunshine-api.revoke');
  }

  get connectButtonLabel() {
    return this.isSaving
      ? this.intl.t('standalone.zendesk.settings.sunshine-api.connecting')
      : this.intl.t('standalone.zendesk.settings.sunshine-api.connect');
  }

  private setupOAuthMessageHandler() {
    this.oauthMessageHandler = (
      event: MessageEvent<{ type: 'OAUTH_SUCCESS' | 'OAUTH_ERROR'; error?: string }>,
    ) => {
      // Ensure the message is from our domain
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'OAUTH_SUCCESS') {
        this.handleOAuthCallback();
      } else if (event.data.type === 'OAUTH_ERROR') {
        this.handleOAuthCallback(event.data.error);
      }
    };

    window.addEventListener('message', this.oauthMessageHandler);
  }

  private async handleOAuthCallback(error?: string) {
    // Clean up OAuth state immediately to prevent multiple calls
    this.cleanupOAuthState();

    if (error) {
      console.error(error);
      this.notificationsService.notifyError(
        this.intl.t('standalone.zendesk.settings.connect.oauth-failed', { error }),
      );
      return;
    }

    try {
      // Save zendesk config to get updated state.
      await this.zendeskPlatform.saveZendeskConfig();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.zendesk.settings.sunshine-api.oauth-success'),
      );
      this.args.closeOnSave?.();
    } catch (e) {
      this.notificationsService.notifyError(
        this.intl.t('standalone.zendesk.settings.sunshine-api.config-reload-error'),
      );
    }
  }

  private cleanupOAuthState() {
    this.isConnecting = false;

    if (this.oauthMessageHandler) {
      window.removeEventListener('message', this.oauthMessageHandler);
      this.oauthMessageHandler = undefined;
    }

    if (this.oauthWindow) {
      this.oauthWindow.close();
      this.oauthWindow = null;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Zendesk::Settings::SunshineApi': typeof SunshineApi;
    'standalone/zendesk/settings/sunshine-api': typeof SunshineApi;
  }
}
